<template>
  <section class="info-card" :class="{ 'info-card--flush': flush }">
    <header v-if="hasHeader" class="info-card__head">
      <div class="info-card__title">
        <slot name="title">
          <h2 v-if="title" class="info-card__title-text">{{ title }}</h2>
        </slot>
      </div>
      <div v-if="hasActions" class="info-card__actions">
        <slot name="actions" />
      </div>
    </header>
    <div class="info-card__body">
      <slot />
    </div>
  </section>
</template>

<script lang="ts" setup>
import { computed, useSlots } from 'vue';

const props = withDefaults(
  defineProps<{
    /** Optional plain-text title; ignored when the `title` slot is supplied. */
    title?: string;
    /** Drops body padding so tables / charts can bleed to the card edges. */
    flush?: boolean;
  }>(),
  {
    title: '',
    flush: false,
  },
);

const slots = useSlots();

const hasActions = computed(() => Boolean(slots.actions));
const hasHeader = computed(() => Boolean(slots.title) || Boolean(props.title) || hasActions.value);
</script>

<style lang="scss" scoped>
.info-card {
  background: var(--bui-tbl-row-fill, #fffcfd);
  border-radius: var(--bui-tbl-card-radius, 12px);
  padding: 1rem 1.25rem;
  box-shadow: var(--bui-tbl-card-shadow, 0 8px 32px rgba(75, 24, 134, 0.16));
  color: var(--bui-tbl-row-text, #2a2969);
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-width: 0;
}

.info-card--flush {
  padding: 0;

  .info-card__head {
    padding: 1rem 1.25rem 0;
  }

  .info-card__body {
    padding: 0;
  }
}

.info-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  min-height: 28px;
}

.info-card__title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}

.info-card__title-text {
  margin: 0;
  font-family: inherit;
  font-size: 1rem;
  font-weight: 700;
  color: var(--bui-tbl-row-text, #2a2969);
  line-height: 1.2;
}

:slotted(.info-card__title-text) {
  margin: 0;
  font-family: inherit;
  font-size: 1rem;
  font-weight: 700;
  color: var(--bui-tbl-row-text, #2a2969);
  line-height: 1.2;
}

.info-card__actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.info-card__body {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
</style>
